import { callOutcomeLabel } from "@/lib/callOutcomeLabel";
import { callActorLabel } from "@/lib/callActorLabel";
import ErrorComponent from "@/components/atoms/ErrorComponent";
import Flex from "@/components/atoms/Flex";
import Loader from "@/components/atoms/Loader";
import SoftBadge from "@/components/atoms/SoftBadge";
import CallRecordingPlayer from "@/components/molecules/CallRecordingPlayer";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { PaginateCardProps } from "@/interfaces/Card";
import CallLog, { CallLogOutcome } from "@/interfaces/CallLog";
import { IFilters } from "@/interfaces/Fetch";
import { useGetCallLogsQuery } from "@/services/callLogs";
import {
  faArrowDown,
  faArrowUp,
  faClock,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DateTime } from "luxon";
import { FC, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import PaginateCard from "../PaginateCard";

const OUTCOME_BADGE: Record<CallLogOutcome, { label: string; bg: string }> = {
  ringing: { label: "Sonne", bg: "info" },
  in_call: { label: "En cours", bg: "info" },
  completed: { label: "Terminé", bg: "success" },
  canceled: { label: "Annulé", bg: "warning" },
  failed: { label: "Échoué", bg: "danger" },
};

const directionLabel = (direction: CallLog["direction"]) =>
  direction === "in" ? "Entrant" : direction === "out" ? "Sortant" : "Inconnu";

const formatDuration = (durationS: number | null): string =>
  durationS == null ? "—" : `${durationS}s`;

/** Exactly one of these identifies the record whose calls are listed — the
 *  three Correspondant types a call can be matched to (see CONTEXT.md). */
interface RecordScope {
  companyId?: string;
  personId?: string;
  instructorId?: string;
}

const RecordCallLogsCard: FC<PaginateCardProps & RecordScope> = ({
  companyId,
  personId,
  instructorId,
  onGetTotal,
}) => {
  const [filters, setFilters] = useState<IFilters>({
    pageIndex: 0,
    pageSize: 10,
  });

  // The search input is uncontrolled and fires on every keystroke; debouncing
  // keeps one request per pause instead of one per character.
  const debouncedQuery = useDebouncedValue(filters.query, 500);

  const { data, isLoading, isError, error } = useGetCallLogsQuery({
    ...filters,
    query: debouncedQuery,
    companyId,
    personId,
    instructorId,
  });

  useEffect(() => {
    data?.meta && onGetTotal?.(data.meta.total);
  }, [data?.meta]);

  if (isLoading) {
    return <Loader />;
  } else if (isError) {
    return <ErrorComponent error={error} />;
  }

  return (
    <PaginateCard meta={data?.meta} filters={filters} setFilters={setFilters}>
      {!data?.data.length && (
        <p className="text-600 mb-0">Aucun appel pour cette fiche.</p>
      )}
      {data?.data.map((callLog) => {
        const badge = {
          ...OUTCOME_BADGE[callLog.outcome],
          label: callOutcomeLabel(callLog),
        };
        return (
          <div
            key={callLog.id}
            className="bg-white dark__bg-1100 p-x1 rounded-3 shadow-sm"
          >
            <Row className="flex-between-center">
              <Col xs={"auto"}>
                <Flex alignItems="center" className="gap-2">
                  <FontAwesomeIcon
                    icon={callLog.direction === "in" ? faArrowDown : faArrowUp}
                    className={
                      callLog.direction === "in"
                        ? "text-success"
                        : "text-primary"
                    }
                    transform="shrink-2"
                  />
                  <p className="fw-semi-bold mb-0">
                    {directionLabel(callLog.direction)}
                    {callLog.remoteNumber && (
                      <span className="text-600 fw-normal">
                        &nbsp;·&nbsp;{callLog.remoteNumber}
                      </span>
                    )}
                  </p>
                </Flex>
              </Col>
              <Col xs={"auto"}>
                <SoftBadge bg={badge.bg}>{badge.label}</SoftBadge>
              </Col>
            </Row>

            <div className="border-bottom border-0 my-3"></div>

            <Row className="flex-between-center g-2">
              <Col xs={12} lg={"auto"}>
                <Flex alignItems="center" className="gap-3 flex-wrap">
                  <h6 className="d-flex align-items-center gap-1 mb-0">
                    <FontAwesomeIcon icon={faClock} transform="shrink-3 up-1" />
                    <span className="text-600">
                      {DateTime.fromISO(callLog.createdAt)
                        .setLocale("fr")
                        .toFormat("dd LLL yyyy HH:mm")}
                    </span>
                  </h6>
                  <h6 className="d-flex align-items-center gap-1 mb-0">
                    <FontAwesomeIcon icon={faPhone} transform="shrink-3 up-1" />
                    <span className="text-600">
                      {callLog.softphoneLine?.label ??
                        callLog.extension ??
                        "Ligne inconnue"}
                    </span>
                  </h6>
                  <span className="text-600 fs--1">
                    {callActorLabel(callLog)}
                  </span>
                  <span className="text-600 fs--1">
                    Durée&nbsp;: {formatDuration(callLog.durationS)}
                  </span>
                </Flex>
              </Col>
              {callLog.recordingFile?.link && (
                <Col xs={12} lg={"auto"}>
                  <CallRecordingPlayer src={callLog.recordingFile.link} />
                </Col>
              )}
            </Row>
          </div>
        );
      })}
    </PaginateCard>
  );
};

export default RecordCallLogsCard;
